import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Button,
  Empty,
  Tabs,
  Modal,
  message,
  Tag,
  Spin,
  Popconfirm
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import CategoryForm from '../components/store/CategoryForm'
import ProductCard from '../components/store/ProductCard'
import { getProducts, getCategories, deleteCategory } from '../api/store'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import api from '../utils/api'

const PageContainer = styled.div`
  padding: 24px;
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const Title = styled.h1`
  margin: 0;
  color: #1a1a1a;
  font-size: 24px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
    background: white;
    padding: 0 16px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 24px;
  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr); /* 固定兩列 */
    gap: 12px; /* 手機版可以縮小間距 */
  }
`

const EmptyState = styled(Empty)`
  margin: 48px 0;
`

const StatusTag = styled(Tag)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
`

const StorePage = () => {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  // 分類 tab 以網址為準：從編輯商品頁返回時元件會重新掛載，
  // 只有寫在網址上的分類還原得回來。
  const [activeTab, setActiveTab] = useState(
    () => searchParams.get('tab') || 'all'
  )
  const [loading, setLoading] = useState(true)

  // 獲取產品和分類數據
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          getProducts(),
          getCategories()
        ])
        setProducts(productsRes.data?.data?.items || [])
        setCategories(
          Array.isArray(categoriesRes.data.data) ? categoriesRes.data.data : []
        )
      } catch (error) {
        message.error('獲取數據失敗')
        setProducts([])
        setCategories([])
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // 分類寫回網址，用 replace（同一畫面的參數微調，不佔瀏覽器歷史）
  useEffect(() => {
    const next = new URLSearchParams()
    if (activeTab !== 'all') next.set('tab', activeTab)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // 進編輯／新增頁時帶上來源網址，返回鍵在沒有站內歷史時才有正確的退路
  const listLocation = `${location.pathname}${location.search}`

  console.log(products)

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size='large' />
        </div>
      </PageContainer>
    )
  }

  const handleAddProduct = () => {
    if (categories.length === 0) {
      message.warning('請先添加商品分類')
      return
    }
    navigate('/store/add', { state: { from: listLocation } })
  }

  const handleTabChange = (key) => {
    setActiveTab(key)
  }

  // 刷新分類列表
  const refreshCategories = async () => {
    try {
      const categoriesRes = await getCategories()
      setCategories(
        Array.isArray(categoriesRes.data.data) ? categoriesRes.data.data : []
      )
    } catch (error) {
      console.error('獲取分類失敗：', error)
    }
  }

  // 處理編輯分類
  const handleEditCategory = (category) => {
    setEditingCategory(category)
    setIsCategoryModalVisible(true)
  }

  // 處理刪除分類
  const handleDeleteCategory = async (categoryId) => {
    try {
      await deleteCategory(categoryId)
      message.success('分類已刪除')
      await refreshCategories()
      // 如果刪除的是當前選中的分類，切換到全部商品
      if (activeTab === categoryId) {
        setActiveTab('all')
      }
    } catch (error) {
      console.error('刪除分類失敗：', error)
      message.error(error.response?.data?.message || '刪除分類失敗')
    }
  }

  // 生成標籤頁數據
  const tabItems = [
    { key: 'all', label: '全部商品' },
    ...categories.map((cat) => ({
      key: cat._id,
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8
          }}
        >
          <span>{cat.name}</span>
          <div
            style={{ display: 'flex', gap: 4 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type='text'
              size='small'
              icon={<EditOutlined />}
              onClick={() => handleEditCategory(cat)}
              style={{ padding: '0 4px' }}
            />
            <Popconfirm
              title='確定要刪除此分類嗎？'
              description='刪除分類後，該分類下的商品將不會被刪除，但會失去分類標記。'
              onConfirm={() => handleDeleteCategory(cat._id)}
              okText='確定'
              cancelText='取消'
            >
              <Button
                type='text'
                size='small'
                danger
                icon={<DeleteOutlined />}
                style={{ padding: '0 4px' }}
              />
            </Popconfirm>
          </div>
        </div>
      )
    }))
  ]

  // 添加刪除商品的處理函數
  const handleDeleteProduct = async (productId) => {
    try {
      await api.delete(`/api/store/products/${productId}`)
      message.success('商品已刪除')
      // 重新獲取商品列表
      const productsRes = await getProducts()
      setProducts(productsRes.data?.data?.items || [])
    } catch (error) {
      console.error('刪除商品失敗：', error)
      message.error('刪除失敗')
    }
  }

  return (
    <PageContainer>
      <HeaderSection>
        <Title>商品管理</Title>
        <ButtonGroup>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => setIsCategoryModalVisible(true)}
          >
            新增分類
          </Button>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={handleAddProduct}
          >
            新增/採購商品
          </Button>
        </ButtonGroup>
      </HeaderSection>

      <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        type='card'
      />

      <ProductGrid>
        {products.length > 0 ? (
          products
            .filter((product) =>
              activeTab === 'all' ? true : product.categoryId === activeTab
            )
            .map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onDelete={handleDeleteProduct}
              >
                <StatusTag
                  color={product.status === 'active' ? 'success' : 'default'}
                >
                  {product.status === 'active' ? '已上架' : '已下架'}
                </StatusTag>
              </ProductCard>
            ))
        ) : (
          <EmptyState
            description='暫無商品'
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </ProductGrid>

      <Modal
        title={editingCategory ? '編輯分類' : '添加分類'}
        open={isCategoryModalVisible}
        onCancel={() => {
          setIsCategoryModalVisible(false)
          setEditingCategory(null)
        }}
        footer={null}
      >
        <CategoryForm
          category={editingCategory}
          onSuccess={() => {
            setIsCategoryModalVisible(false)
            setEditingCategory(null)
            refreshCategories()
          }}
          onCancel={() => {
            setIsCategoryModalVisible(false)
            setEditingCategory(null)
          }}
        />
      </Modal>
    </PageContainer>
  )
}

export default StorePage
